import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Header from "../../header/header";
import {
    Chat,
    LanguageCode,
    Persona,
    PersonaContent,
    PersonaReels,
    PersonaReelStims,
    PersonaStims,
    PersonaStory,
    PersonaVideo,
    PersonaVideoAd,
    PersonaWatch,
    PersonaWatchStims,
    Profile
} from "../../models";
import {Button, LinearProgress} from "@mui/material";
import {
    getChats,
    getLanguageCodes,
    getPersona,
    getPersonaVideoAds,
    getPersonaVideos,
    unlinkVideo, unlinkVideoAd,
    updatePersona, updatePersonaLinks
} from "../../api";
import {Link, useParams} from "react-router-dom";
import {PersonaForm} from "../components/PersonaForm";
import {useSnackbar} from "notistack";
import {PersonaContentCard} from "../components/PersonaContentCard";
import {PersonaContentBrowserDialog} from "../components/PersonaContentBrowserDialog";
import {VideoFormDialog} from "../../content/components/VideoFormDialog";
import {getProfiles} from "../../api/profiles";
import {VideoAdFormDialog} from "../../content/components/VideoAdFormDialog";
import Content from "../../api/content";
import {PersonaTopBar} from "../components/PersonaTopBar";
import {PersonaContentEdit} from "../components/PersonaContentEdit";

type Page = | "content" | "stims" | "stories" | "watch" | "watchStims" | "reel" | "reelStims" | "video" | "videoStims";
type EditType = | "content" | "stim" | "story" | "watch" | "watchStim" | "reel" | "reelStim" | "video" | "videoStim" | "music" | "musicStim" | "gmail" | "yt" | "ytStim";

interface API {
    content: string;
    stim: string;
}

export const PersonaDetailPage: React.FC = () => {
    const {id} = useParams();

    const {enqueueSnackbar} = useSnackbar();

    // Data state
    const [persona, setPersona] = useState<Persona | null>(null);
    const [tags, setTags] = useState<any[] | null>([]);
    const [languageCodes, setLanguageCodes] = useState<LanguageCode[] | null>(null);
    const [profiles, setProfiles] = useState<Profile[] | null>(null);
    const [chats, setChats] = useState<Chat[] | null>(null);
    const [videos, setVideos] = useState<PersonaVideo[] | null>(null);
    const [videoAds, setVideoAds] = useState<PersonaVideoAd[] | null>(null);
    const [content, setContent] = useState<PersonaContent[] | null>([]);
    const [stims, setStims] = useState<PersonaStims[] | null>([]);
    const [stories, setStories] = useState<PersonaStory[] | null>([]);
    const [watch, setWatch] = useState<PersonaWatch[] | null>([]);
    const [watchStims, setWatchStims] = useState<PersonaWatchStims[] | null>([]);
    const [reels, setReels] = useState<PersonaReels[] | null>([]);
    const [reelStims, setReelStims] = useState<PersonaReelStims[] | null>([]);
    const [error, setError] = useState<any | null>(null);
    const [curContent, setCurContent] = useState<any[] | null>([])
    const [linkingChanges, setLinkingChanges] = useState<any[]>([])

    // Form
    const [personaErrors, setPersonaErrors] = useState<Record<keyof Persona, string>>();
    const [dirty, setDirty] = useState(false);

    const [page, setPage] = useState<Page>("content");
    const pageRef = useRef<Page>("content");
    const [openContentBrowser, setOpenContentBrowser] = useState(false);

    const [editId, setEditId] = useState<number | null>(null);
    const [editType, setEditType] = useState<EditType | null>(null);

    // Derived state
    // const loading = useMemo(() => !(persona && languageCodes && profiles && videos && videoAds) && !error, [persona, languageCodes, profiles, videos, videoAds, error]);
    const changePage = (p: Page) => {
        if (!persona || !persona.type) return

        pageRef.current = p
        setPage(p)

        switch (p) {
            case "content":
                setCurContent(content)
                break
            case "stims":
                setCurContent(stims)
                break
            case "stories":
                setCurContent(stories)
                break
            case "watch":
                setCurContent(watch)
                break
            case "watchStims":
                setCurContent(watchStims)
                break
            case "reel":
                setCurContent(reels)
                break
            case "reelStims":
                setCurContent(reelStims)
                break
            case "video":
                setCurContent(videos)
                break
            case "videoStims":
                setCurContent(videoAds)
                break
            default:
                enqueueSnackbar("Message Devs Immediately Error Code: P2001", {variant: "error"});
        }
    }

    const setCurContentForPage = useCallback((p: Page, pageContent: Partial<Record<Page, any[]>>) => {
        switch (p) {
            case "content":
                setCurContent(pageContent.content ?? [])
                break
            case "stims":
                setCurContent(pageContent.stims ?? [])
                break
            case "stories":
                setCurContent(pageContent.stories ?? [])
                break
            case "watch":
                setCurContent(pageContent.watch ?? [])
                break
            case "watchStims":
                setCurContent(pageContent.watchStims ?? [])
                break
            case "reel":
                setCurContent(pageContent.reel ?? [])
                break
            case "reelStims":
                setCurContent(pageContent.reelStims ?? [])
                break
            case "video":
                setCurContent(pageContent.video ?? [])
                break
            case "videoStims":
                setCurContent(pageContent.videoStims ?? [])
                break
            default:
                enqueueSnackbar("Message Devs Immediately Error Code: P2001", {variant: "error"});
        }
    }, [enqueueSnackbar])

    const loading = useMemo(() => {
        if (error) return false;
        if (!persona) return true;

        switch (persona.type) {
            case "default":
                return !(content && stims && stories && watch && watchStims && reels && reelStims && curContent && profiles && languageCodes);
            case "reel":
            case "streamer":
                return !(content && stims && curContent)
            case "gmail":
            case "article":
            case "music":
                return !(content && curContent)
            case "video":
                return !(videos && videoAds && curContent && languageCodes && profiles)
            default:
                return true;
        }
    }, [persona, languageCodes, profiles, videos, videoAds, videoAds, error, content, stims]);

    const [submitting, setSubmitting] = useState(false);
    const [reloading, setReloading] = useState(false);

    const getType = (): API => {
        let api: API = {
            content: "content", stim: "stim"
        }

        switch (persona?.type) {
            case "reel":
                api.content = "reel"
                api.stim = "reelStim"
                break
            case "gmail":
                api.content = "gmail"
                api.stim = "stim"
                break
            case "music":
                api.content = "music"
                api.stim = "musicStim"
                break
            case "article":
                api.content = "article"
                api.stim = "stim"
                break
            case "streamer":
                api.content = "streamer"
                api.stim = "streamerStim"
                break
            default:
                // Do nothing
                break
        }

        return api
    }

    const fetchData = useCallback(async (refresh: boolean = false) => {
        setError(null);
        setReloading(true);

        try {
            const [data, languageCodes, profiles] = await Promise.all([await getPersona(Number(id), refresh), await getLanguageCodes(), await getProfiles(),]);

            switch (data.persona.type) {
                case "default":
                    setContent(data.content);
                    setStims(data.stims);
                    setStories(data.stories);
                    setWatch(data.watch);
                    setWatchStims(data.watchStims);
                    setReels(data.reels);
                    setReelStims(data.reelStims);
                    if (!refresh) {
                        setCurContent(data.content)
                    } else {
                        setCurContentForPage(pageRef.current, {
                            content: data.content,
                            stims: data.stims,
                            stories: data.stories,
                            watch: data.watch,
                            watchStims: data.watchStims,
                            reel: data.reels,
                            reelStims: data.reelStims,
                        })
                    }
                    break;

                case "reel":
                case "streamer":
                    setContent(data.content)
                    setStims(data.stims)
                    if (!refresh) {
                        setCurContent(data.content)
                    } else {
                        setCurContentForPage(pageRef.current, {
                            content: data.content,
                            stims: data.stims,
                        })
                    }
                    break
                case "gmail":
                case "music":
                case "article":
                    setContent(data.content)
                    if (!refresh) {
                        setCurContent(data.content)
                    } else {
                        setCurContentForPage(pageRef.current, {
                            content: data.content,
                        })
                    }
                    break

                case "video":
                    // Persona Type Video
                    const [chats, videos, videoAds] = await Promise.all([await getChats(), await getPersonaVideos(Number(id)), await getPersonaVideoAds(Number(id)),]);

                    if (!refresh) {
                        pageRef.current = "video"
                        setPage("video")
                    }
                    setChats(chats);
                    setVideos(videos);
                    setVideoAds(videoAds);
                    if (!refresh) {
                        setCurContent(data.content)
                    } else {
                        setCurContentForPage(pageRef.current, {
                            video: videos,
                            videoStims: videoAds,
                        })
                    }
                    break;
                default:
                    enqueueSnackbar("Message Devs Immediately Error Code: P2002", {variant: "error"});
            }
            setPersona(data.persona);
            setTags(data.tags)
            setLanguageCodes(languageCodes);
            setProfiles(profiles);
        } catch (error) {
            setError(error);
        } finally {
            setReloading(false);
        }
    }, [id, setCurContentForPage]);

    // Fetch data on mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    function validate() {
        const errors: Partial<Record<keyof Persona, string>> = {};

        if (!persona?.name) {
            errors.name = "Name is required";
        } else if (persona.name.length > 255) {
            errors.name = "Name must be less than 255 characters";
        }

        if (!persona?.profileName) {
            errors.profileName = "Profile Name is required";
        } else if (persona.profileName.length > 255) {
            errors.profileName = "Profile Name must be less than 255 characters";
        }

        if (!persona?.profilePictureUrl) {
            errors.profilePictureUrl = "Profile Picture URL is required";
        }

        if (!persona?.type) {
            errors.type = "Type is required";
        }

        if (!persona?.languageCodeId) {
            errors.languageCodeId = "Language Code is required";
        }

        if (Object.keys(errors).length > 0) {
            setPersonaErrors(errors as Record<keyof Persona, string>);
        } else {
            setPersonaErrors(undefined);
        }

        return Object.keys(errors).length === 0;
    }

    function handlePersonaChange(persona: Persona) {
        if (dirty) {
            validate();
        }
        setPersona(persona);
    }

    type TagId = Persona['tagIds'] extends (infer T)[] ? T : never;

    function updateTags(newValue: { id?: TagId }[]): void {
        setPersona(prev => {
            if (!prev) return prev

            const tagIds = newValue
                .map(item => item.id)
                .filter((id): id is TagId => id !== undefined && id !== null)

            return {
                ...prev,
                tagIds,
            }
        })
    }

    async function handlePersonaSave() {
        if (!validate()) {
            setDirty(true);
            return;
        }

        setDirty(false);

        enqueueSnackbar("Saving...", {variant: "info"});

        setSubmitting(true);

        try {
            await updatePersona(Number(id), persona!);
            await updatePersonaLinks(linkingChanges)
            enqueueSnackbar("Persona saved", {variant: "success"});
            await fetchData(true);
        } catch (e) {
            enqueueSnackbar("Failed to save persona", {variant: "error"});
            console.error(e);
        }

        setSubmitting(false);
    }

    //@ts-ignore
    const updateOrderAndLock = (setState, lc) => {
        //@ts-ignore
        setState(prev =>
            //@ts-ignore
            prev.map(item =>
                item.id === lc.id
                    ? { ...item, order: lc.order, locked: lc.locked }
                    : item
            )
        )

        setCurContent(prev =>
            //@ts-ignore
            prev.map(item =>
                item.id === lc.id
                    ? { ...item, order: lc.order, locked: lc.locked }
                    : item
            ))
    }

    function handleLinkageChange (contentId: number, linkage: { order: number, locked: boolean }) {
        const item = curContent!.find((v) => v.id === contentId);

        if (!item) {
            return;
        }

        let api = getType()
        let type = ""

        const pageConfig = {
            content: { type: api.content, setter: setContent },
            stims: { type: api.stim, setter: setStims },
            stories: { type: "story", setter: setStories },
            watch: { type: "watch", setter: setWatch },
            watchStims: { type: "watchStim", setter: setWatchStims },
            reel: { type: "reel", setter: setReels },
            reelStims: { type: "reelStim", setter: setReelStims },
            video: { type: "video", setter: setVideos },
            videoStims: { type: "videoStim", setter: setVideoAds },
        }

        const config = pageConfig[page]

        let lc = {
            id: contentId,
            persona: id,
            order: linkage.order,
            locked: linkage.locked,
            type: type
        }

        if (config) {
            lc.type = config.type
            if (config.setter) updateOrderAndLock(config.setter, lc)
        } else {
            enqueueSnackbar("Message Devs Immediately Error Code: P2005", {variant: "error"});
        }

        setLinkingChanges(prev => {
            const exists = prev.some(l => l.id === lc.id);
            return exists
                ? prev.map(l => (l.id === lc.id ? lc : l))
                : [...prev, lc];
        });
    }

    function handleEdit(id: number, type: EditType) {
        setEditId(id)
        setEditType(type)
    }

    async function handleUnlink(content: any) {
        enqueueSnackbar("Unlinking content...", {variant: "info"});

        let api = getType()

        const pageConfig = {
            content: { type: api.content, setter: setContent },
            stims: { type: api.stim, setter: setStims },
            stories: { type: "story", setter: setStories },
            watch: { type: "watch", setter: setWatch },
            watchStims: { type: "watchStim", setter: setWatchStims },
            reel: { type: "reel", setter: setReels },
            reelStims: { type: "reelStim", setter: setReelStims },
            video: { type: "video", setter: setVideos },
            videoStims: { type: "videoStim", setter: setVideoAds },
        }

        const config = pageConfig[page]

        try {
            if (config) {
                if (config.type === "video") {
                    await unlinkVideo(Number(id), content.id);
                } else if (config.type === "videoStim") {
                    await unlinkVideoAd(Number(id), content.id);
                } else {
                    await Content.unlinkContentNew(id, content.id, config.type)
                }
            } else {
                enqueueSnackbar("Message Devs Immediately Error Code: P2004", {variant: "error"});
            }
            await fetchData(true);
            enqueueSnackbar("Content unlinked", {variant: "success"});
        } catch (e) {
            enqueueSnackbar("Failed to unlink content", {variant: "error"});
            console.error(e);
        }
    }

    return (<>
            <Header/>
            {loading && <div style={{width: "50%"}}>
                <LinearProgress/>
            </div>}
            {error && <div>Error: {error}</div>}
            {!loading && !error && <div className="social">
                <PersonaTopBar personaType={persona?.type} page={page} changePage={changePage} handlePersonaSave={handlePersonaSave} setOpenContentBrowser={setOpenContentBrowser} submitting={submitting} />
                <PersonaForm
                    persona={persona!}
                    languageCodes={languageCodes!}
                    onPersonaChange={handlePersonaChange}
                    updateTags={updateTags}
                    errors={personaErrors}
                    tags={tags}
                />
                <div className="box persona-edit">
                    <div>
                        {curContent!
                            .sort((a, b) => a.order - b.order)
                            .map((content) => (
                                <PersonaContentCard
                                    key={content.id}
                                    item={content}
                                    onLinkageChange={handleLinkageChange}
                                    onItemEdit={handleEdit}
                                    onItemUnlink={handleUnlink}
                                    // @ts-ignore
                                    personaType={persona.type}
                                    page={page}
                                    disabled={reloading}
                                />
                            ))}
                    </div>
                </div>
            </div>}
            {openContentBrowser && <PersonaContentBrowserDialog
                open={openContentBrowser}
                onOpenChange={() => setOpenContentBrowser(false)}
                onLinkedContent={() => fetchData(true)}
                pageData={page}
                //@ts-ignore
                personaType={persona.type}
                currentContent={curContent ?? []}
                disabled={reloading}
            />}
            {editId && editType && <PersonaContentEdit
                editId={editId}
                editType={editType}
                languageCodes={languageCodes!}
                profiles={profiles!}
                chats={chats!}
                onClose={() => {
                    setEditId(null)
                    setEditType(null)
                }}
                onSave={() => fetchData()}
            />}
        </>);
};
